
import { useRef, useState, useEffect } from "react";
import { useFrame, useLoader } from "@react-three/fiber";
import { TextureLoader } from "three";
import { Html } from "@react-three/drei";
import { Mesh, Group } from "three";

// Import refactored components
import UserMarker from "./globe/UserMarker";
import ParticleRing from "./globe/ParticleRing";
import GlobeAtmosphere from "./globe/GlobeAtmosphere";
import RotationControl from "./globe/RotationControl";

// Import hooks
import { useLocation } from "../hooks/useLocation";
import { useUsers } from "../hooks/useUsers";

interface PlanetProps {
  position?: [number, number, number];
  size?: number;
  rotationSpeed?: number;
}

const Planet = ({
  position = [0, 0, 0],
  size = 4,
  rotationSpeed = 0.0005,
}: PlanetProps) => {
  const planetRef = useRef<Mesh>(null);
  const markersGroupRef = useRef<Group>(null);
  const [autoRotate, setAutoRotate] = useState<boolean>(true);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  
  // Get user location data
  const { userLocation, userMarkerPosition, ipAddress } = useLocation(size);
  
  // Get users data
  const users = useUsers(size, userMarkerPosition, ipAddress);
  
  // Earth texture
  const earthMap = useLoader(
    TextureLoader, 
    "https://unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
  );

  // Create a markers group to rotate with the planet
  useEffect(() => {
    if (planetRef.current && markersGroupRef.current) {
      // Ensure the markers group has the same rotation as the planet
      markersGroupRef.current.rotation.copy(planetRef.current.rotation);
    }
  }, []);

  // Handle planet rotation
  useFrame(() => {
    if (planetRef.current && autoRotate) {
      planetRef.current.rotation.y += rotationSpeed;
      
      // Sync markers group with planet rotation
      if (markersGroupRef.current) {
        markersGroupRef.current.rotation.copy(planetRef.current.rotation);
      }
    }
  });

  // Focus on user's location when detected
  useEffect(() => {
    if (userLocation && planetRef.current && userMarkerPosition && !autoRotate) {
      const [lat, long] = userLocation;
      const rotationY = -((long + 180) % 360) * (Math.PI / 180);
      planetRef.current.rotation.y = rotationY;
      
      // Sync markers with new rotation
      if (markersGroupRef.current) {
        markersGroupRef.current.rotation.copy(planetRef.current.rotation);
      }
    }
  }, [userLocation, userMarkerPosition, autoRotate]);

  const handleToggleRotation = () => {
    setAutoRotate(!autoRotate);
  };

  const handleMarkerClick = (userId: string) => {
    setSelectedUser(userId === selectedUser ? null : userId);
    
    // Find the user
    const clickedUser = users.find(user => user.id === userId);
    
    if (clickedUser && clickedUser.lat !== undefined && clickedUser.long !== undefined) {
      // Stop rotation to focus on the selected location
      setAutoRotate(false);
      
      // Rotate planet to show the clicked location
      if (planetRef.current) {
        const rotationY = -((clickedUser.long + 180) % 360) * (Math.PI / 180);
        planetRef.current.rotation.y = rotationY;
        
        // Update marker group rotation
        if (markersGroupRef.current) {
          markersGroupRef.current.rotation.copy(planetRef.current.rotation);
        }
      }
    }
  };

  return (
    <group>
      {/* Earth sphere */}
      <mesh ref={planetRef} position={position}>
        <sphereGeometry args={[size, 64, 64]} />
        <meshStandardMaterial 
          map={earthMap}
          metalness={0.1}
          roughness={0.7}
          emissive="#0c1e3a"
          emissiveIntensity={0.05}
        />
      </mesh>
      
      {/* Atmosphere glow */}
      <GlobeAtmosphere size={size} />
      
      {/* Particle cloud effect */}
      <ParticleRing size={size} />
      
      {/* All user markers */}
      <group ref={markersGroupRef} position={position}>
        {users.map((user) => (
          <UserMarker 
            key={user.id}
            position={user.position} 
            data={{
              id: user.id,
              name: user.name,
              ip: user.ip,
              isCurrentUser: user.isCurrentUser,
              lat: user.lat,
              long: user.long
            }}
            color={user.isCurrentUser ? "#ea384c" : "#60a5fa"}
            isSelected={selectedUser === user.id}
            onClick={() => handleMarkerClick(user.id)}
            alwaysShowInfo={user.isCurrentUser}
          />
        ))}
      </group>

      {/* Rotation control button */}
      <RotationControl 
        size={size}
        autoRotate={autoRotate}
        onToggleRotation={handleToggleRotation}
      />
    </group>
  );
};

export default Planet;
